import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { BriefcaseBusiness, CheckCircle2, ClipboardList, Loader2, Moon, Sparkles, X } from "lucide-react";
import {
  getMyTasksApi,
  getTodayDailyWorkLogApi,
  saveMorningDailyWorkLogApi,
  savePunchOutDailyWorkLogApi,
} from "../app/api";

const activeStatuses = new Set(["TODO", "IN_PROGRESS", "REVIEW", "HOLD", "PENDING"]);

export default function DailyWorkLogModal({ open, mode = "morning", onClose, onSaved }) {
  const isPunchOut = mode === "punchOut";
  const [tasks, setTasks] = useState([]);
  const [todayLog, setTodayLog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    task_id: "",
    custom_title: "",
    morning_description: "",
    extra_details: "",
    punch_out_description: "",
  });

  useEffect(() => {
    if (!open) return;

    let mounted = true;
    setLoading(true);
    setError("");

    Promise.all([getMyTasksApi(), getTodayDailyWorkLogApi()])
      .then(([taskRes, logRes]) => {
        if (!mounted) return;

        const parsedTasks = (taskRes?.data || []).filter((task) =>
          activeStatuses.has(String(task.status || "").toUpperCase())
        );
        const log = logRes?.data || null;

        setTasks(parsedTasks);
        setTodayLog(log);
        setForm({
          task_id: log?.task_id ? String(log.task_id) : "",
          custom_title: log?.custom_title || "",
          morning_description: log?.morning_description || "",
          extra_details: log?.extra_details || "",
          punch_out_description: log?.punch_out_description || "",
        });
      })
      .catch(() => {
        if (mounted) setError("Unable to load today's work details.");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [open]);

  const selectedTask = useMemo(
    () => tasks.find((task) => String(task.id) === String(form.task_id)),
    [tasks, form.task_id]
  );

  const updateForm = (field, value) => {
    setError("");
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");

    const payload = isPunchOut
      ? { punch_out_description: form.punch_out_description }
      : {
          task_id: form.task_id || null,
          custom_title: form.custom_title,
          morning_description: form.morning_description,
          extra_details: form.extra_details,
        };

    const res = isPunchOut
      ? await savePunchOutDailyWorkLogApi(payload)
      : await saveMorningDailyWorkLogApi(payload);

    setSaving(false);

    if (!res?.success) {
      setError(res?.message || "Please complete the required details.");
      return;
    }

    onSaved?.(res.data);
    onClose?.();
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[2147483647] flex items-center justify-center bg-[#05010f]/78 px-3 py-2 text-white backdrop-blur-xl sm:px-4">
      <div className="relative flex h-[calc(100vh-28px)] max-h-[860px] w-full max-w-2xl flex-col overflow-hidden rounded-[22px] border border-white/15 bg-[#160d32] shadow-[0_32px_110px_rgba(0,0,0,0.65)] sm:h-[calc(100vh-40px)] sm:rounded-[26px]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_0%,rgba(34,211,238,0.22),transparent_34%),radial-gradient(circle_at_92%_8%,rgba(168,85,247,0.28),transparent_34%)]" />

        <div className="relative shrink-0 border-b border-white/10 bg-white/6 px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3 sm:gap-4">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-cyan-400 to-violet-600 shadow-[0_18px_44px_rgba(34,211,238,0.24)] sm:h-12 sm:w-12">
                {isPunchOut ? <Moon className="h-5 w-5 sm:h-6 sm:w-6" /> : <ClipboardList className="h-5 w-5 sm:h-6 sm:w-6" />}
              </div>
              <div className="min-w-0">
                <p className="text-[9px] font-black uppercase tracking-[0.22em] text-cyan-100/68 sm:text-[10px]">
                  {isPunchOut ? "End of day" : "Morning plan"}
                </p>
                <h2 className="mt-0.5 text-lg font-black tracking-tight sm:text-xl">
                  {isPunchOut ? "What did you finish today?" : "What will you work on today?"}
                </h2>
                <p className="mt-0.5 line-clamp-1 text-xs leading-5 text-white/58">
                  {isPunchOut
                    ? "Review your punch-in plan and add the final work summary."
                    : "Select an existing task or add a fresh title for today's work."}
                </p>
              </div>
            </div>

            {!isPunchOut && (
              <button
                type="button"
                onClick={onClose}
                className="grid h-9 w-9 shrink-0 place-items-center rounded-2xl border border-white/15 bg-white/8 text-white/70 transition hover:bg-white/14 hover:text-white sm:h-10 sm:w-10"
                aria-label="Close daily work popup"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        <div className="relative min-h-0 flex-1 overflow-y-auto px-4 py-3 sm:px-6 sm:py-4">
          {loading ? (
            <div className="grid min-h-[280px] place-items-center text-white/60">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <div className="space-y-3">
              {isPunchOut && (
                <div className="rounded-[22px] border border-cyan-200/16 bg-cyan-300/8 p-4 sm:rounded-3xl">
                  <div className="mb-3 flex items-center gap-2 text-cyan-100">
                    <CheckCircle2 className="h-5 w-5" />
                    <span className="text-xs font-black uppercase tracking-[0.22em]">Morning punch-in plan</span>
                  </div>
                  <h3 className="text-lg font-black">
                    {todayLog?.task_title || todayLog?.custom_title || "No morning task title added"}
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-white/68">
                    {todayLog?.morning_description || "Morning description was not filled yet."}
                  </p>
                  {todayLog?.extra_details && (
                    <p className="mt-3 rounded-2xl bg-white/8 p-3 text-sm text-white/62">
                      {todayLog.extra_details}
                    </p>
                  )}
                </div>
              )}

              {!isPunchOut && (
                <>
                  <div className="rounded-[20px] border border-white/10 bg-white/7 p-3 sm:p-3.5">
                    <label className="mb-2 flex items-center gap-2 text-sm font-bold text-white/82">
                      <BriefcaseBusiness className="h-4 w-4 text-cyan-200" />
                      Select existing task
                    </label>
                    <select
                      value={form.task_id}
                      onChange={(event) => updateForm("task_id", event.target.value)}
                      className="h-10 w-full rounded-2xl border border-white/12 bg-[#241845] px-4 text-sm text-white outline-none transition focus:border-cyan-200/70 sm:h-11"
                    >
                      <option value="">No existing task selected</option>
                      {tasks.map((task) => (
                        <option key={task.id} value={task.id}>
                          {task.title}
                        </option>
                      ))}
                    </select>
                    {selectedTask && (
                      <p className="mt-2 text-xs leading-5 text-white/48 line-clamp-2">
                        {selectedTask.description || "No description on this task yet."}
                      </p>
                    )}
                  </div>

                  <div className="rounded-[20px] border border-white/10 bg-white/7 p-3 sm:p-3.5">
                    <label className="mb-2 block text-sm font-bold text-white/82">
                      New task title
                    </label>
                    <input
                      value={form.custom_title}
                      onChange={(event) => updateForm("custom_title", event.target.value)}
                      placeholder="e.g. Making new game"
                      className="h-10 w-full rounded-2xl border border-white/12 bg-[#241845] px-4 text-sm text-white outline-none placeholder:text-white/35 transition focus:border-cyan-200/70 sm:h-11"
                    />
                    <p className="mt-1.5 text-xs text-white/42">
                      Use this when your work is not in the existing task list.
                    </p>
                  </div>

                  <div className="rounded-[20px] border border-white/10 bg-white/7 p-3 sm:p-3.5">
                    <label className="mb-2 block text-sm font-bold text-white/82">
                      What will you do today?
                    </label>
                    <textarea
                      value={form.morning_description}
                      onChange={(event) => updateForm("morning_description", event.target.value)}
                      rows={2}
                      placeholder="Write the work plan for today..."
                      className="w-full resize-none rounded-2xl border border-white/12 bg-[#241845] px-4 py-3 text-sm leading-6 text-white outline-none placeholder:text-white/35 transition focus:border-cyan-200/70"
                    />
                  </div>

                  <details className="rounded-[20px] border border-white/10 bg-white/7 p-3 sm:p-3.5">
                    <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-bold text-white/82">
                      <Sparkles className="h-4 w-4 text-amber-200" />
                      Extra details
                      <span className="ml-auto text-xs font-semibold text-white/38">Optional</span>
                    </summary>
                    <div className="mt-3">
                      <textarea
                        value={form.extra_details}
                        onChange={(event) => updateForm("extra_details", event.target.value)}
                        rows={2}
                        placeholder="Optional blockers, links, priorities, or notes..."
                        className="w-full resize-none rounded-2xl border border-white/12 bg-[#241845] px-4 py-3 text-sm leading-6 text-white outline-none placeholder:text-white/35 transition focus:border-cyan-200/70"
                      />
                    </div>
                  </details>
                  {/* <div className="rounded-[22px] border border-white/10 bg-white/7 p-3 sm:rounded-3xl sm:p-4">
                    <label className="mb-2 flex items-center gap-2 text-sm font-bold text-white/82">
                      <Sparkles className="h-4 w-4 text-amber-200" />
                      Extra details
                    </label>
                    <textarea
                      value={form.extra_details}
                      onChange={(event) => updateForm("extra_details", event.target.value)}
                      rows={2}
                      placeholder="Optional blockers, links, priorities, or notes..."
                      className="w-full resize-none rounded-2xl border border-white/12 bg-[#241845] px-4 py-3 text-sm leading-6 text-white outline-none placeholder:text-white/35 transition focus:border-cyan-200/70"
                    />
                  </div> */}
                </>
              )}

              {isPunchOut && (
                <div className="rounded-[22px] border border-white/10 bg-white/7 p-3 sm:rounded-3xl sm:p-4">
                  <label className="mb-2 block text-sm font-bold text-white/82">
                    Punch-out summary
                  </label>
                  <textarea
                    value={form.punch_out_description}
                    onChange={(event) => updateForm("punch_out_description", event.target.value)}
                    rows={4}
                    placeholder="Write what was completed, pending, or blocked today..."
                    className="w-full resize-none rounded-2xl border border-white/12 bg-[#241845] px-4 py-3 text-sm leading-6 text-white outline-none placeholder:text-white/35 transition focus:border-cyan-200/70"
                  />
                </div>
              )}

              {error && (
                <div className="rounded-2xl border border-red-300/25 bg-red-400/12 px-4 py-3 text-sm font-semibold text-red-100">
                  {error}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="relative flex shrink-0 items-center justify-end gap-2 border-t border-white/10 bg-[#0f0824]/95 px-4 py-2.5 sm:px-6">
          {!isPunchOut && (
            <button
              type="button"
              onClick={onClose}
              className="h-10 rounded-2xl border border-white/14 px-4 text-sm font-black text-white/72 transition hover:bg-white/8"
            >
              Later
            </button>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={loading || saving}
            className="h-10 rounded-2xl bg-gradient-to-r from-cyan-400 to-violet-600 px-4 text-sm font-black text-white shadow-[0_18px_42px_rgba(34,211,238,0.2)] transition hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-60 sm:px-6"
          >
            {saving ? "Saving..." : isPunchOut ? "Save and finish day" : "Save today's plan"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
